import jsPDF from "jspdf";
import dayjs from "dayjs";

export const formatApplicantNames = (applicants: Applicant[]): string => {
  if (!applicants || applicants.length === 0) {
    return "Unknown Applicant";
  }

  if (applicants.length === 1) {
    const applicant = applicants[0];
    return applicant
      ? `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim()
      : "Unknown Applicant";
  }

  return (
    applicants
      .map((applicant) =>
        `${applicant.first_name || ""} ${applicant.last_name || ""}`.trim()
      )
      .filter((name) => name)
      .join(" | ") || "Multiple Applicants"
  );
}

export const formatDate = (dateStr: any) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export const formatDate2 = (dateString?: string) => {
    if (!dateString) return "N/A";
    
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "numeric"
      });
    } catch (error) {
      return "Invalid date";
    }
};

export function getFormattedDate(dateInput: Date): string {
  try {
    const date = new Date(dateInput);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date");
    }
    // Format time
    const time = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });

    // Get date parts
    const month = date.toLocaleString("en-US", { month: "long" });
    const day = date.getDate();
    const year = date.getFullYear();

    // Add ordinal suffix to day
    const ordinalSuffix =
      day % 10 === 1 && day !== 11
        ? "st"
        : day % 10 === 2 && day !== 12
          ? "nd"
          : day % 10 === 3 && day !== 13
            ? "rd"
            : "th";

    return `${time} on ${month} ${day}${ordinalSuffix}, ${year}`;
  } catch (error) {
    return "Unknown date";
  }
}

/**
 * Parse a date string to extract year, month, and day components.
 * This prevents timezone-related day shifts by treating dates as calendar dates.
 * 
 * @param dateString - The date string to parse
 * @returns An object with year, month (1-12), and day, or null if invalid
 */
export function parseLocalDateComponents(dateString: string): { year: number; month: number; day: number } | null {
  if (!dateString) return null;

  // Try YYYY-MM-DD format (with optional time component)
  const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const y = parseInt(year, 10);
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
      return { year: y, month: m, day: d };
    }
  }

  // Try MM/DD/YYYY format (US format)
  const usMatch = dateString.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (usMatch) {
    const [, month, day, year] = usMatch;
    const m = parseInt(month, 10);
    const d = parseInt(day, 10);
    const y = parseInt(year, 10);
    if (m >= 1 && m <= 12 && d >= 1 && d <= 31 && y >= 1900 && y <= 2100) {
      return { year: y, month: m, day: d };
    }
  }

  return null;
}

/**
 * Parse a date string to a Date object using local timezone.
 * Extracts date components directly to prevent timezone-related day shifts.
 * 
 * IMPORTANT: This function treats dates as calendar dates (date-only),
 * not as specific moments in time. This ensures that a date of birth
 * like "1990-01-15" stays as January 15th regardless of timezone.
 */
export function parseLocalDate(value: string | Date | undefined): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const components = parseLocalDateComponents(value);
  if (!components) return null;

  // Create date using local timezone (month is 0-indexed)
  return new Date(components.year, components.month - 1, components.day);
}

export function formatPDFDate(value: string | Date | undefined): string {
  if (!value) return "N/A";
  
  let day: number;
  let month: string;
  let year: number;
  
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "N/A";
    day = value.getDate();
    month = value.toLocaleString("en-US", { month: "long" });
    year = value.getFullYear();
  } else {
    // Parse string using local timezone to prevent day shifts
    const components = parseLocalDateComponents(value);
    if (!components) return "N/A";
    
    day = components.day;
    year = components.year;
    // Create a date object just to get the month name
    const tempDate = new Date(components.year, components.month - 1, components.day);
    month = tempDate.toLocaleString("en-US", { month: "long" });
  }
  
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
        ? "nd"
        : day % 10 === 3 && day !== 13
          ? "rd"
          : "th";
  return `${month} ${day}${suffix}, ${year}`;
}

export function getColorFromRecommendation(num: number | undefined): string {
  if (num === undefined) return "gray";
  const colors = [
    "green",
    "lightgreen",
    "yellowgreen",
    "red",
    "orange",
    "gray",
    "red",
  ];
  return colors[num - 1] || "gray";
}

export function getColorFromApplicantRecommendation(
  value: string | undefined
): string {
  if (!value) return "gray";

  switch (value.toLowerCase()) {
    case "accept":
      return "green";
    case "refer":
      return "orange";
    case "decline":
      return "red";
    case "pending":
      return "gray";
    case "error":
      return "red";
    case "cancelled":
      return "darkgray";
    default:
      return "gray";
  }
}

export const calculateLeaseEndDate = (moveInDate: any, leaseTerm: number) => {
  if (!moveInDate || !leaseTerm) return null;

  const start = dayjs(moveInDate).startOf("day");
  let end = start.add(leaseTerm, "month").endOf("month").startOf("day");

  if (start.date() < 16) {
    end = start.add(leaseTerm - 1, "month").endOf("month").startOf("day");
  }

  return end;
};

/**
 * Rounds a number using banker's rounding (round half to even).
 * This matches Python's round() behavior for consistency with the cost sheet.
 */
const bankersRound = (value: number): number => {
  const rounded = Math.round(value);
  // Check if we're exactly at .5
  if (Math.abs(value - Math.floor(value) - 0.5) < Number.EPSILON) {
    // Round to even
    return Math.floor(value) % 2 === 0 ? Math.floor(value) : Math.ceil(value);
  }
  return rounded;
};

export const calculateProration = (moveInDate: any, amount: number, feeName: string) => {
  if (!moveInDate?.isValid() || !amount) return null;

  const moveInDay = moveInDate.date();

  if (moveInDay === 1) {
    return {
      description: `Full ${feeName}`,
      amount: bankersRound(amount),
    };
  }

  const daysInMonth = moveInDate.daysInMonth();

  const prorateStart = daysInMonth - 31;
  const day2ProrateAmount = amount + (amount / 30) * prorateStart;
  const rawProrated = day2ProrateAmount - (amount / 30) * (moveInDay - 2);

  const proratedAmount = bankersRound(rawProrated);

  return {
    description: `Prorated ${feeName}`,
    amount: proratedAmount,
  };
};

export const getApplicantRoleSuggestion = (applicant: any, index: number, unitRent: number, incomeData: any) => {
  const payrollIncome = incomeData?.applicantsIncome?.[index]?.monthlyIncome ?? null;
  const bankIncome = incomeData?.applicantsBankIncome?.[index]?.included_income ?? null;
  const monthlyIncome = payrollIncome || bankIncome || 0;
  // Use parseLocalDate to avoid timezone-related age calculation errors
  const dobDate = applicant.dob ? parseLocalDate(applicant.dob) : null;
  const age = dobDate ? dayjs().diff(dayjs(dobDate), 'year') : 18;
  const isGuarantor = applicant.gurantor;
  const isMinor = age < 18;

  if (isMinor) {
    return { role: 'Occupant', reason: 'Applicant is under 18' };
  }
  if ((isGuarantor && monthlyIncome >= (unitRent ?? 1) * 2) || monthlyIncome >= (unitRent ?? 1) * 2) {
    return { role: 'Leaseholder', reason: 'Sufficient income (≥ 1/2 of rent)' };
  }
  return { role: 'Occupant', reason: 'Insufficient income for leaseholder' };
};

export const getStatusDisplay = (status: string) => {
  switch (status.toLowerCase()) {
    case "created":
      return { text: "Pending", color: "#6c757d" };
    case "sent":
      return { text: "Sent", color: "#008fc7ff" };
    case "delivered":
      return { text: "Viewed", color: "#c8d41eff" };
    case "completed":
      return { text: "Signed", color: "#3dbe31ff" };
    case "declined":
      return { text: "Declined", color: "#dc3545" };
    case "authenticationfailed":
      return { text: "Authentication Failed", color: "#b02a37" };
    case "autoresponded":
      return { text: "Auto Responded", color: "#6f42c1" };
    default:
      return { text: "Pending", color: "#adb5bd" };
  }
}

export const getSignerTimestamp = (signer: any): string | null => {
  // DocuSign timestamps are in PST - return the most relevant timestamp based on status
  const status = signer.status?.toLowerCase();
  
  let timestamp: string | null = null;
  
  if (status === "completed" && signer.signedDateTime) {
    timestamp = signer.signedDateTime;
  } else if (status === "delivered" && signer.deliveredDateTime) {
    timestamp = signer.deliveredDateTime;
  } else if (status === "sent" && signer.sentDateTime) {
    timestamp = signer.sentDateTime;
  } else if (status === "declined" && signer.declinedDateTime) {
    timestamp = signer.declinedDateTime;
  }
  
  if (!timestamp) return null;
  
  try {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
}

export function generateGroupPDF(
  selectedAppGroup: any,
  selectedAppGroupPlaidResults: any,
  selectedAppGroupScreening: any
): void {
  const doc = new jsPDF();
  let yPos = 15;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  const lineHeight = 5;

  // Enhanced color palette
  const colors = {
    primary: [45, 57, 74],      // #2D394A
    secondary: [74, 144, 226],   // #4A90E2
    accent: [0, 200, 81],        // #00C851
    success: [76, 175, 80],      // #4CAF50
    warning: [255, 152, 0],      // #FF9800
    danger: [244, 67, 54],       // #F44336
    text: [51, 51, 51],          // #333333
    textLight: [117, 117, 117],  // #757575
    lightGray: [248, 249, 250],  // #F8F9FA
    border: [224, 224, 224],     // #E0E0E0
    white: [255, 255, 255],
  };

  // Helper: Check if value is empty/N/A
  const isEmpty = (val: any): boolean => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string') {
      const trimmed = val.trim().toLowerCase();
      return trimmed === '' || trimmed === 'n/a' || trimmed === 'null' || trimmed === 'undefined';
    }
    return false;
  };

  // Helper: Get display value (returns null if empty to skip)
  const getDisplayValue = (val: any, fallback?: string): string | null => {
    if (isEmpty(val)) return fallback ?? null;
    return String(val);
  };

  // Helper: Check for page break
  const checkPageBreak = (requiredHeight: number = 15): boolean => {
    if (yPos + requiredHeight > pageHeight - 25) {
      doc.addPage();
      yPos = 15;
      return true;
    }
    return false;
  };

  // Helper: Add a status badge
  const addBadge = (text: string, xPos: number, yPos: number, type: 'success' | 'warning' | 'danger' | 'info' = 'info'): number => {
    const badgeColors = {
      success: colors.success,
      warning: colors.warning,
      danger: colors.danger,
      info: colors.secondary,
    };
    const bgColor = badgeColors[type];

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    const textWidth = doc.getTextWidth(text);
    const badgeWidth = textWidth + 6;
    const badgeHeight = 5;

    doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
    doc.roundedRect(xPos, yPos - 3.5, badgeWidth, badgeHeight, 1, 1, 'F');

    doc.setTextColor(255, 255, 255);
    doc.text(text, xPos + 3, yPos);
    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);

    return badgeWidth;
  };

  // Helper: Add styled header (more compact)
  const addHeader = (title: string, level: number = 1, badge?: { text: string; type: 'success' | 'warning' | 'danger' | 'info' }): void => {
    checkPageBreak(level === 1 ? 20 : 12);
    if (yPos > 15 && level <= 2) yPos += level === 1 ? 8 : 5;

    const fontSize = level === 1 ? 14 : level === 2 ? 11 : 9;
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', 'bold');

    if (level === 1) {
      doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      doc.rect(margin, yPos - 4, contentWidth, 10, 'F');

      doc.setFillColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      doc.rect(margin, yPos - 4, 2.5, 10, 'F');

      doc.setTextColor(255, 255, 255);
      doc.text(title, margin + 6, yPos + 2);

      if (badge) {
        const titleWidth = doc.getTextWidth(title);
        addBadge(badge.text, margin + titleWidth + 12, yPos + 2, badge.type);
      }

      yPos += 14;
    } else if (level === 2) {
      doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
      doc.rect(margin, yPos - 3, contentWidth, 8, 'F');

      doc.setFillColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      doc.rect(margin, yPos - 3, 2, 8, 'F');

      doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      doc.text(title, margin + 5, yPos + 2);

      if (badge) {
        const titleWidth = doc.getTextWidth(title);
        addBadge(badge.text, margin + titleWidth + 10, yPos + 2, badge.type);
      }

      yPos += 10;
    } else {
      doc.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      doc.text(title, margin, yPos);
      yPos += 6;
    }

    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
    doc.setFont('helvetica', 'normal');
  };

  // Helper: Add compact info grid (filters out empty values)
  const addInfoGrid = (pairs: Array<{ key: string; value: string | null; highlight?: boolean }>, columns: number = 2): void => {
    // Filter out null/empty values
    const validPairs = pairs.filter(p => p.value !== null && !isEmpty(p.value));
    if (validPairs.length === 0) return;

    const rowHeight = 6;
    const rows = Math.ceil(validPairs.length / columns);
    const cardHeight = rows * rowHeight + 6;
    
    checkPageBreak(cardHeight + 5);
    const cardStartY = yPos;

    doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
    doc.roundedRect(margin, cardStartY, contentWidth, cardHeight, 1.5, 1.5, 'F');

    doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
    doc.setLineWidth(0.2);
    doc.roundedRect(margin, cardStartY, contentWidth, cardHeight, 1.5, 1.5, 'S');

    yPos = cardStartY + 4;

    const colWidth = contentWidth / columns;
    doc.setFontSize(8);

    validPairs.forEach((pair, i) => {
      const col = i % columns;
      const xPos = margin + 4 + (col * colWidth);

      if (col === 0 && i > 0) {
        yPos += rowHeight;
        checkPageBreak(8);
      }

      // Key
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
      const keyText = pair.key + ':';
      doc.text(keyText, xPos, yPos);

      // Value - truncate if too long
      doc.setFont('helvetica', pair.highlight ? 'bold' : 'normal');
      doc.setTextColor(pair.highlight ? colors.primary[0] : colors.text[0],
        pair.highlight ? colors.primary[1] : colors.text[1],
        pair.highlight ? colors.primary[2] : colors.text[2]);

      const keyWidth = doc.getTextWidth(keyText);
      const availWidth = colWidth - keyWidth - 8;
      let displayVal = pair.value || '';
      
      // Truncate long values with ellipsis
      if (doc.getTextWidth(displayVal) > availWidth) {
        while (doc.getTextWidth(displayVal + '...') > availWidth && displayVal.length > 0) {
          displayVal = displayVal.slice(0, -1);
        }
        displayVal += '...';
      }

      doc.text(displayVal, xPos + keyWidth + 2, yPos);
    });

    yPos = cardStartY + cardHeight + 4;
    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
  };

  // Helper: Add compact data table
  const addTable = (headers: string[], rows: string[][], columnWidths?: number[]): void => {
    if (rows.length === 0) return;
    checkPageBreak(30);

    const tableWidth = contentWidth;
    const colWidths = columnWidths || headers.map(() => tableWidth / headers.length);
    const rowHeight = 6;
    const headerHeight = 7;

    // Table header
    doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
    doc.rect(margin, yPos, tableWidth, headerHeight, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);

    let xPos = margin + 2;
    headers.forEach((header, i) => {
      doc.text(header, xPos, yPos + 5);
      xPos += colWidths[i];
    });

    yPos += headerHeight;

    // Table rows
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);

    rows.forEach((row, rowIdx) => {
      checkPageBreak(rowHeight + 3);

      if (rowIdx % 2 === 0) {
        doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
        doc.rect(margin, yPos, tableWidth, rowHeight, 'F');
      }

      doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
      doc.setLineWidth(0.1);
      doc.line(margin, yPos, margin + tableWidth, yPos);

      xPos = margin + 2;
      row.forEach((cell, cellIdx) => {
        let cellText = cell || '';
        const maxWidth = colWidths[cellIdx] - 4;
        if (doc.getTextWidth(cellText) > maxWidth) {
          while (doc.getTextWidth(cellText + '...') > maxWidth && cellText.length > 0) {
            cellText = cellText.slice(0, -1);
          }
          cellText += '...';
        }
        doc.text(cellText, xPos, yPos + 4);
        xPos += colWidths[cellIdx];
      });

      yPos += rowHeight;
    });

    doc.line(margin, yPos, margin + tableWidth, yPos);
    yPos += 6;
  };

  // Helper: Add thin divider
  const addDivider = (): void => {
    checkPageBreak(6);
    doc.setDrawColor(colors.border[0], colors.border[1], colors.border[2]);
    doc.setLineWidth(0.2);
    doc.line(margin + 10, yPos, pageWidth - margin - 10, yPos);
    yPos += 6;
  };

  // Helper: Format applicant names
  const formatApplicantNames = (names: Array<{ first_name: string; last_name: string }> = []): string =>
    names.map((n) => `${n.first_name} ${n.last_name}`).join(', ') || 'N/A';

  // Helper: Format address compactly
  const formatAddress = (...parts: any[]): string | null => {
    const filtered = parts.filter(p => !isEmpty(p));
    return filtered.length > 0 ? filtered.join(', ') : null;
  };

  // ===== COVER PAGE =====
  doc.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.rect(0, 0, pageWidth, 60, 'F');

  doc.setFillColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
  doc.circle(pageWidth - 20, 20, 30, 'F');

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('PROPERTY MANAGEMENT', margin, 20);

  yPos = 80;
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
  doc.text('Application Report', pageWidth / 2, yPos, { align: 'center' });

  yPos += 12;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
  doc.text('Group Application Analysis', pageWidth / 2, yPos, { align: 'center' });

  yPos += 20;
  const boxHeight = 50;
  doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
  doc.roundedRect(margin + 15, yPos, contentWidth - 30, boxHeight, 2, 2, 'F');

  yPos += 12;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(colors.text[0], colors.text[1], colors.text[2]);

  const coverInfo = [
    `Property: ${selectedAppGroup.property_name || 'N/A'}`,
    `Applicants: ${formatApplicantNames(selectedAppGroup.applicant_names)}`,
    `Generated: ${getFormattedDate(new Date())}`,
  ];

  coverInfo.forEach((info) => {
    doc.text(info, pageWidth / 2, yPos, { align: 'center' });
    yPos += 9;
  });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
  doc.text('Confidential Document', pageWidth / 2, pageHeight - 15, { align: 'center' });

  // ===== PAGE 2: SCREENING SUMMARY (MOVED TO TOP) + GROUP SUMMARY =====
  doc.addPage();
  yPos = 15;

  const { applicants = [], desired_lease_term = 'N/A', desired_move_in_date, status = 'N/A' } = selectedAppGroup;
  const { data = {} } = selectedAppGroupPlaidResults;

  // ===== SCREENING SUMMARY - NOW AT THE TOP =====
  if (selectedAppGroupScreening.Applicants?.length > 0) {
    addHeader('Screening Summary', 1);

    const screeningHeaders = ['Applicant', 'Score', 'Criminal', 'Eviction'];
    const screeningRows = selectedAppGroupScreening.Applicants.map((screening: any) => {
      const matchedApplicant = applicants.find((a: any) => a.application_id === screening.ApplicantId);
      return [
        matchedApplicant ? `${matchedApplicant.first_name} ${matchedApplicant.last_name}` : 'N/A',
        screening.ScoreResult?.ApplicantScore || 'N/A',
        screening.CriminalRecommendation || 'N/A',
        screening.EvictionRecommendation || 'N/A',
      ];
    });

    addTable(screeningHeaders, screeningRows, [55, 35, 45, 45]);
    addDivider();
  }

  // ===== APPLICATION GROUP SUMMARY =====
  const statusBadgeType = status.toLowerCase().includes('approved') ? 'success' :
    status.toLowerCase().includes('pending') ? 'warning' : 'info';

  addHeader('Application Group Summary', 1, { text: status, type: statusBadgeType });

  const groupSummary = [
    { key: 'Property', value: getDisplayValue(selectedAppGroup.property_name), highlight: true },
    { key: 'Applicants', value: formatApplicantNames(selectedAppGroup.applicant_names), highlight: true },
    { key: 'Desired Unit', value: getDisplayValue(applicants[0]?.desired_unit_number) },
    { key: 'Lease Term', value: typeof desired_lease_term === 'number' ? `${desired_lease_term} months` : getDisplayValue(desired_lease_term) },
    { key: 'Move-In Date', value: desired_move_in_date ? formatDate(desired_move_in_date) : null },
    { key: 'Monthly Income', value: data.groupMonthlyIncome ? `$${data.groupMonthlyIncome.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null, highlight: true },
    { key: 'Annual Income', value: data.groupMonthlyIncome ? `$${(data.groupMonthlyIncome * 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null, highlight: true },
    { key: 'Plaid Verified', value: !!(data.applicantsIdv?.length || data.applicantsIncome?.length || data.applicantsBankIncome?.length) ? '✓ Yes' : '✗ No' },
  ];

  addInfoGrid(groupSummary, 2);

  // Financial Overview (compact)
  const totalMonthlyIncome = data.groupMonthlyIncome || 0;
  const estimatedRent = applicants[0]?.current_residence_rent || 0;
  const incomeToRentRatio = estimatedRent > 0 ? (totalMonthlyIncome / estimatedRent).toFixed(2) : null;

  if (totalMonthlyIncome > 0 || incomeToRentRatio) {
    addHeader('Financial Overview', 2);
    const financialSummary = [
      { key: 'Total Monthly Income', value: totalMonthlyIncome > 0 ? `$${totalMonthlyIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null, highlight: true },
      { key: 'Total Annual Income', value: totalMonthlyIncome > 0 ? `$${(totalMonthlyIncome * 12).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null, highlight: true },
      { key: 'Applicants', value: applicants.length.toString() },
      { key: 'Income-to-Rent Ratio', value: incomeToRentRatio ? `${incomeToRentRatio}x` : null, highlight: true },
      { key: 'Plaid Verified', value: `${data.applicantsIncome?.filter((a: any) => a).length || 0} of ${applicants.length}` },
    ];
    addInfoGrid(financialSummary, 2);
  }

  // ===== INDIVIDUAL APPLICANTS =====
  applicants.forEach((applicant: any, index: number) => {
    const idv = data.applicantsIdv?.[index] || null;
    const payroll = data.applicantsIncome?.[index] || null;
    const bank = data.applicantsBankIncome?.[index] || null;
    const completePayroll = data.completePayrollIncome?.[index] || null;
    const completeBank = data.completeBankIncome?.[index] || null;
    const screening = selectedAppGroupScreening.Applicants?.find(
      (a: any) => a.ApplicantId === applicant.application_id
    ) || {};

    doc.addPage();
    yPos = 15;

    addHeader(`${applicant.first_name} ${applicant.last_name}`, 1);

    // Personal Information (only non-empty fields)
    addHeader('Personal Information', 2);
    const personalInfo = [
      { key: 'Full Name', value: [applicant.first_name, applicant.middle_name, applicant.last_name].filter(Boolean).join(' ') || null },
      { key: 'Date of Birth', value: applicant.dob ? formatPDFDate(applicant.dob) : null },
      { key: 'Phone', value: getDisplayValue(applicant.phone_number) },
      { key: 'Email', value: getDisplayValue(applicant.email) },
      { key: 'SSN', value: getDisplayValue(applicant.social_security_number) },
      { key: 'Other Names', value: getDisplayValue(applicant.other_names) },
      { key: 'Employment', value: getDisplayValue(applicant.employment_status) },
      { key: 'Annual Income', value: applicant.employment_income ? `$${applicant.employment_income.toLocaleString()}` : null, highlight: true },
      { key: 'Job Title', value: getDisplayValue(applicant.employment_title) },
      { key: 'Employer', value: getDisplayValue(applicant.employment_employer) },
      { key: 'Employer Email', value: getDisplayValue(applicant.employment_email) },
      { key: 'Employer Phone', value: getDisplayValue(applicant.employment_phone_number) },
      { key: 'Industry', value: getDisplayValue(applicant.employment_industry) },
      { key: 'Marital Status', value: getDisplayValue(applicant.misc_marital_status) },
      { key: 'Smoker', value: applicant.misc_smoker !== undefined ? (applicant.misc_smoker ? 'Yes' : 'No') : null },
      { key: 'Has Guarantor', value: applicant.gurantor !== undefined ? (applicant.gurantor ? 'Yes' : 'No') : null },
    ];
    addInfoGrid(personalInfo, 2);

    // Miscellaneous/Background Information
    const hasMiscInfo = 
      applicant.misc_crimes !== undefined ||
      applicant.misc_bankruptcy !== undefined ||
      applicant.misc_evicted !== undefined ||
      applicant.misc_outstanding_payments !== undefined ||
      applicant.misc_legal_residents !== undefined ||
      applicant.misc_has_facebook_profile !== undefined;

    if (hasMiscInfo) {
      addHeader('Background Information', 2);
      const miscInfo = [
        { key: 'Criminal History', value: applicant.misc_crimes !== undefined ? (applicant.misc_crimes ? 'Yes' : 'No') : null, highlight: applicant.misc_crimes === true },
        { key: 'Crime Details', value: applicant.misc_crimes ? getDisplayValue(applicant.misc_crimes_details) : null },
        { key: 'Bankruptcy History', value: applicant.misc_bankruptcy !== undefined ? (applicant.misc_bankruptcy ? 'Yes' : 'No') : null, highlight: applicant.misc_bankruptcy === true },
        { key: 'Bankruptcy Details', value: applicant.misc_bankruptcy ? getDisplayValue(applicant.misc_bankruptcy_details) : null },
        { key: 'Prior Eviction', value: applicant.misc_evicted !== undefined ? (applicant.misc_evicted ? 'Yes' : 'No') : null, highlight: applicant.misc_evicted === true },
        { key: 'Outstanding Landlord Payments', value: applicant.misc_outstanding_payments !== undefined ? (applicant.misc_outstanding_payments ? 'Yes' : 'No') : null, highlight: applicant.misc_outstanding_payments === true },
        { key: 'Legal US Residents', value: applicant.misc_legal_residents !== undefined ? (applicant.misc_legal_residents ? 'Yes' : 'No') : null },
        { key: 'Has Facebook Profile', value: applicant.misc_has_facebook_profile !== undefined ? (applicant.misc_has_facebook_profile ? 'Yes' : 'No') : null },
        { key: 'Facebook Profile', value: applicant.misc_has_facebook_profile ? getDisplayValue(applicant.misc_facebook_profile) : null },
      ];
      addInfoGrid(miscInfo, 2);
    }

    // Dependents (only if exists) - support both 'children' and 'minors' field names
    const childrenData = applicant.children || applicant.minors || [];
    const normalizedChildren = Array.isArray(childrenData)
      ? childrenData.map((child: any) => {
          // If child is a string (e.g., "Emily Doe"), parse it
          if (typeof child === "string") {
            const nameParts = child.trim().split(" ");
            return {
              first_name: nameParts[0] || "",
              last_name: nameParts.slice(1).join(" ") || "",
              dob: null,
            };
          }
          return child;
        })
      : [];

    if (normalizedChildren.length > 0) {
      addHeader('Dependents', 2);
      const childHeaders = ['Name', 'Age', 'Date of Birth'];
      const childRows = normalizedChildren.map((child: any) => {
        const firstName = child.first_name || child.firstName || '';
        const lastName = child.last_name || child.lastName || '';
        const dob = child.dob || child.dateOfBirth;
        // Use parseLocalDate to avoid timezone-related age calculation errors
        const dobDate = dob ? parseLocalDate(dob) : null;
        const age = dobDate ? dayjs().diff(dayjs(dobDate), 'year') : null;
        return [
          [firstName, lastName].filter(Boolean).join(' ') || 'N/A',
          age !== null ? `${age} yrs` : 'N/A',
          dob ? formatPDFDate(dob) : 'N/A',
        ];
      });
      addTable(childHeaders, childRows, [70, 35, 75]);
    }

    // Current Residence (only if has address data)
    const currentAddress = formatAddress(
      applicant.current_residence_address,
      applicant.current_residence_unit,
      applicant.current_residence_city,
      applicant.current_residence_state,
      applicant.current_residence_zip
    );
    
    if (currentAddress || applicant.current_residence_rent || applicant.current_residence_landlord_name) {
      addHeader('Current Residence', 2);
      const residenceInfo = [
        { key: 'Address', value: currentAddress },
        { key: 'Monthly Rent', value: applicant.current_residence_rent ? `$${applicant.current_residence_rent.toLocaleString()}` : null },
        { key: 'Move-In Date', value: applicant.current_residence_start_date ? formatPDFDate(applicant.current_residence_start_date) : null },
        { key: 'Landlord', value: getDisplayValue(applicant.current_residence_landlord_name) },
        { key: 'Landlord Phone', value: getDisplayValue(applicant.current_residence_landlord_phone) },
        { key: 'Reason for Leaving', value: getDisplayValue(applicant.current_residence_reason_for_leaving) },
      ];
      addInfoGrid(residenceInfo, 2);
    }

    // Previous Residence (only if exists)
    const prevAddress = formatAddress(
      applicant.other_residence_address,
      applicant.other_residence_unit,
      applicant.other_residence_city,
      applicant.other_residence_state,
      applicant.other_residence_zip
    );
    
    if (prevAddress) {
      addHeader('Previous Residence', 2);
      const prevResidence = [
        { key: 'Address', value: prevAddress },
        { key: 'Monthly Rent', value: applicant.other_residence_rent ? `$${applicant.other_residence_rent.toLocaleString()}` : null },
        { key: 'Move-In Date', value: applicant.other_residence_start_date ? formatPDFDate(applicant.other_residence_start_date) : null },
        { key: 'Landlord', value: getDisplayValue(applicant.other_residence_landlord_name) },
      ];
      addInfoGrid(prevResidence, 2);
    }

    // Emergency Contacts (only if exists)
    if (applicant.emergency_contact_name || applicant.emergency_contact2_name) {
      addHeader('Emergency Contacts', 2);
      const emergencyContacts = [];

      if (applicant.emergency_contact_name) {
        emergencyContacts.push(
          { key: 'Primary', value: getDisplayValue(applicant.emergency_contact_name) },
          { key: 'Phone', value: getDisplayValue(applicant.emergency_contact_phone) },
          { key: 'Relationship', value: getDisplayValue(applicant.emergency_contact_relationship) }
        );
      }

      if (applicant.emergency_contact2_name) {
        emergencyContacts.push(
          { key: 'Secondary', value: getDisplayValue(applicant.emergency_contact2_name) },
          { key: 'Phone', value: getDisplayValue(applicant.emergency_contact2_phone) },
          { key: 'Relationship', value: getDisplayValue(applicant.emergency_contact2_relationship) }
        );
      }

      addInfoGrid(emergencyContacts, 3);
    }

    // Vehicles (only if exists)
    if (Array.isArray(applicant.vehicles) && applicant.vehicles.length > 0) {
      addHeader('Vehicles', 2);
      const vehicleHeaders = ['Make/Model', 'Year', 'Color', 'License', 'Insured'];
      const vehicleRows = applicant.vehicles.map((v: any) => [
        [v.make, v.model].filter(Boolean).join(' ') || 'N/A',
        v.year?.toString() || 'N/A',
        v.color || 'N/A',
        v.lic || 'N/A',
        v.vehicle_insured ? 'Yes' : 'No',
      ]);
      addTable(vehicleHeaders, vehicleRows, [50, 25, 30, 40, 35]);
    }

    // Pets (only if exists)
    if (Array.isArray(applicant.pets) && applicant.pets.length > 0) {
      addHeader('Pets', 2);
      const petHeaders = ['Name', 'Type/Breed', 'Age', 'Weight', 'Neutered'];
      const petRows = applicant.pets.map((p: any) => [
        p.name || 'N/A',
        [p.type, p.breed].filter(Boolean).join(' - ') || 'N/A',
        p.age?.toString() || 'N/A',
        p.weight ? `${p.weight} lbs` : 'N/A',
        p.neutered ? 'Yes' : 'No',
      ]);
      addTable(petHeaders, petRows, [35, 55, 25, 30, 35]);
    }

    // Income Verification (only if has data)
    if ((payroll && payroll.jobs?.length > 0) || (bank && bank.included_income > 0)) {
      addHeader('Income Verification', 2);

      if (payroll && payroll.jobs?.length > 0) {
        const incomeVerification = [
          { key: 'Monthly Income', value: payroll.monthlyIncome ? `$${payroll.monthlyIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null, highlight: true },
          { key: 'YTD Income', value: payroll.ytdIncome ? `$${payroll.ytdIncome.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null, highlight: true },
          { key: 'Active Jobs', value: payroll.jobs?.length?.toString() },
          { key: 'Verification Date', value: payroll.ytdDate ? formatDate(payroll.ytdDate) : null },
        ];
        addInfoGrid(incomeVerification, 2);

        if (payroll.jobs.length > 0) {
          addHeader('Employment Details', 3);
          const jobHeaders = ['Employer', 'Pay Frequency', 'Gross Pay', 'Net Pay'];
          const jobRows = payroll.jobs.map((job: any) => [
            job.employerName || 'N/A',
            job.payFrequency || 'N/A',
            job.grossEarnings ? `$${job.grossEarnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
            job.netPay ? `$${job.netPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
          ]);
          addTable(jobHeaders, jobRows, [55, 40, 40, 45]);
        }
      } else if (bank && bank.included_income > 0) {
        const bankIncome = [
          { key: 'Monthly Income', value: `$${bank.included_income.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, highlight: true },
          { key: 'Excluded Income', value: bank.excluded_income ? `$${bank.excluded_income.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : null },
          { key: 'Income Sources', value: bank.included_sources?.length?.toString() },
        ];
        addInfoGrid(bankIncome, 2);

        if (bank.included_sources?.length > 0) {
          addHeader('Income Sources', 3);
          const sourceHeaders = ['Category', 'Description', 'Monthly Amount'];
          const sourceRows = bank.included_sources.map((s: any) => [
            s.category || 'N/A',
            s.description || 'N/A',
            s.monthly_amount ? `$${s.monthly_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
          ]);
          addTable(sourceHeaders, sourceRows, [50, 75, 55]);
        }
      }
    }

    // Identity Verification (only if has data)
    if (idv?.kyc_check) {
      addHeader('Identity Verification', 2);

      const getVerificationBadge = (summary: string) => summary === 'match' ? '✓ Valid' : '✗ Invalid';

      const idvInfo = [
        { key: 'ID Authenticity', value: idv.kyc_check.id_number?.summary ? getVerificationBadge(idv.kyc_check.id_number.summary) : null },
        { key: 'Name Check', value: idv.kyc_check.name?.summary ? getVerificationBadge(idv.kyc_check.name.summary) : null },
        { key: 'DOB Check', value: idv.kyc_check.date_of_birth?.summary ? getVerificationBadge(idv.kyc_check.date_of_birth.summary) : null },
        { key: 'Address Check', value: idv.kyc_check.address?.summary ? getVerificationBadge(idv.kyc_check.address.summary) : null },
        { key: 'Phone Check', value: idv.kyc_check.phone_number?.summary ? getVerificationBadge(idv.kyc_check.phone_number.summary) : null },
        { key: 'Status', value: getDisplayValue(idv.status), highlight: true },
      ];

      if (idv.risk_check) {
        idvInfo.push(
          { key: 'Bot Detected', value: getDisplayValue(idv.risk_check.behavior?.bot_detected) },
          { key: 'Fraud Ring', value: getDisplayValue(idv.risk_check.behavior?.fraud_ring_detected) },
          { key: 'Email Breaches', value: idv.risk_check.email?.breach_count?.toString() || null }
        );
      }

      addInfoGrid(idvInfo, 3);
    }

    // Screening Results (only if has data)
    if (screening.ScoreResult) {
      addHeader('Screening Results', 2);

      const score = parseInt(screening.ScoreResult.ApplicantScore) || 0;
      const scoreBadgeType = score >= 700 ? 'success' : score >= 600 ? 'warning' : 'danger';

      const screeningInfo = [
        { key: 'Applicant Score', value: getDisplayValue(screening.ScoreResult.ApplicantScore), highlight: true },
        { key: 'Recommendation', value: getDisplayValue(screening.ScoreResult.RecommendationMessage) },
        { key: 'Criminal Check', value: getDisplayValue(screening.CriminalRecommendation) },
        { key: 'Eviction Check', value: getDisplayValue(screening.EvictionRecommendation) },
        { key: 'Status', value: getDisplayValue(screening.ScoreResult.StatusMessage) },
      ];

      if (screening.ScoreResult.CautionNotes?.length > 0) {
        screeningInfo.push({
          key: 'Caution Notes',
          value: screening.ScoreResult.CautionNotes.join(', '),
        });
      }

      addInfoGrid(screeningInfo, 2);

      yPos += 2;
      addBadge(`Score: ${screening.ScoreResult.ApplicantScore}`, margin, yPos, scoreBadgeType);
      yPos += 8;
    }

    // Detailed Income Documentation (only if significant data exists)
    const hasDetailedData =
      (completePayroll && Array.isArray(completePayroll) && completePayroll.some((p: any) => p?.payroll_income?.length > 0)) ||
      (completeBank && completeBank.bank_income?.some((b: any) => b.bank_income_summary?.historical_summary?.length > 0));

    if (hasDetailedData) {
      checkPageBreak(40);
      addHeader('Detailed Income Documentation', 2);

      // Payroll Income Details
      if (completePayroll && Array.isArray(completePayroll)) {
        completePayroll.forEach((payrollSource: any, sourceIndex: number) => {
          if (payrollSource?.payroll_income?.length > 0) {
            payrollSource.payroll_income.forEach((income: any) => {
              if (income.pay_stubs?.length > 0) {
                addHeader(`${payrollSource.institution_name || 'Payroll'} - Pay Stubs`, 3);

                // Sort pay stubs by pay_date descending (most recent first)
                const sortedPayStubs = [...income.pay_stubs]
                  .sort((a: any, b: any) => {
                    const dateA = a.pay_period_details?.pay_date ? new Date(a.pay_period_details.pay_date).getTime() : 0;
                    const dateB = b.pay_period_details?.pay_date ? new Date(b.pay_period_details.pay_date).getTime() : 0;
                    return dateB - dateA; // Descending order
                  })
                  .slice(0, 5);

                const stubHeaders = ['Pay Date', 'Employer', 'Gross', 'Net'];
                const stubRows = sortedPayStubs.map((stub: any) => [
                  stub.pay_period_details?.pay_date ? formatDate(stub.pay_period_details.pay_date) : 'N/A',
                  stub.employer?.name || 'N/A',
                  stub.pay_period_details?.gross_earnings ? `$${stub.pay_period_details.gross_earnings.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
                  stub.net_pay?.current_amount ? `$${stub.net_pay.current_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
                ]);

                addTable(stubHeaders, stubRows, [40, 60, 40, 40]);

                if (income.pay_stubs.length > 5) {
                  doc.setFontSize(8);
                  doc.setFont('helvetica', 'italic');
                  doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
                  doc.text(`+ ${income.pay_stubs.length - 5} more pay stubs`, margin, yPos);
                  yPos += 5;
                }
              }

              if (income.w2s?.length > 0) {
                addHeader('W2 Forms', 3);
                const w2Headers = ['Employer', 'Tax Year', 'Wages', 'Federal Tax'];
                const w2Rows = income.w2s.map((w2: any) => [
                  w2.employer?.name || 'N/A',
                  w2.tax_year || 'N/A',
                  w2.wages ? `$${w2.wages.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
                  w2.federal_income_tax_withheld ? `$${w2.federal_income_tax_withheld.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
                ]);
                addTable(w2Headers, w2Rows, [60, 30, 45, 45]);
              }
            });
          }
        });
      }

      // Bank Income Details (compact) - sorted by most recent first
      if (completeBank && completeBank.bank_income?.length > 0) {
        completeBank.bank_income.forEach((bankIncome: any) => {
          if (bankIncome.bank_income_summary?.historical_summary?.length > 0) {
            addHeader(`${bankIncome.items?.[0]?.institution_name || 'Bank'} - Income`, 3);

            // Sort historical summary by end_date descending (most recent first)
            const sortedSummaries = [...bankIncome.bank_income_summary.historical_summary]
              .sort((a: any, b: any) => {
                const dateA = a.end_date ? new Date(a.end_date).getTime() : 0;
                const dateB = b.end_date ? new Date(b.end_date).getTime() : 0;
                return dateB - dateA; // Descending order
              })
              .slice(0, 2);

            sortedSummaries.forEach((summary: any) => {
              if (summary.transactions?.length > 0) {
                // Sort transactions by date descending (most recent first)
                const sortedTransactions = [...summary.transactions]
                  .sort((a: any, b: any) => {
                    const dateA = a.date ? new Date(a.date).getTime() : 0;
                    const dateB = b.date ? new Date(b.date).getTime() : 0;
                    return dateB - dateA; // Descending order
                  })
                  .slice(0, 5);

                const txnHeaders = ['Date', 'Description', 'Amount'];
                const txnRows = sortedTransactions.map((txn: any) => [
                  formatDate(txn.date) || 'N/A',
                  (txn.name || 'N/A').substring(0, 35),
                  txn.amount ? `$${txn.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : 'N/A',
                ]);
                addTable(txnHeaders, txnRows, [40, 100, 40]);

                if (summary.transactions.length > 5) {
                  doc.setFontSize(8);
                  doc.setFont('helvetica', 'italic');
                  doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
                  doc.text(`+ ${summary.transactions.length - 5} more transactions`, margin, yPos);
                  yPos += 5;
                }
              }
            });
          }
        });
      }
    }
  });

  // ===== DISCLAIMER (at end of last applicant page) =====
  checkPageBreak(25);
  yPos += 5;
  addDivider();
  doc.setFontSize(7);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
  const disclaimer = 'This report is generated for informational purposes only. All information should be verified independently. Income and identity verification data is provided by third-party services (Plaid) and should be reviewed in context with supporting documentation.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth) as string[];
  disclaimerLines.forEach((line: string) => {
    doc.text(line, margin, yPos);
    yPos += 4;
  });

  // ===== FOOTER ON ALL PAGES =====
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    doc.setFillColor(colors.lightGray[0], colors.lightGray[1], colors.lightGray[2]);
    doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');

    doc.setFontSize(7);
    doc.setTextColor(colors.textLight[0], colors.textLight[1], colors.textLight[2]);
    doc.setFont('helvetica', 'normal');

    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, margin, pageHeight - 5);
    doc.text('CONFIDENTIAL', pageWidth - margin, pageHeight - 5, { align: 'right' });
  }

  // ===== SAVE PDF =====
  const applicantNames = formatApplicantNames(selectedAppGroup.applicant_names);
  const fileName = `${applicantNames.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-application-report-${new Date().getTime()}.pdf`;
  doc.save(fileName);
}